/**
 * The greaseproof paper product page: paper, print and quantity picking, the
 * size links, the photos, the artwork brief and Add to cart.
 *
 * The cup page's behaviour (assets/product.js) for sheets: the same per-unit
 * label (three decimals, rounded half up in integer pence maths exactly as
 * greaseproof-product.liquid and snippets/cart-line.liquid), the same total (ex VAT, and with VAT
 * at 20 % on the run and the one-time artwork setup), the same artwork file
 * check (lib/artworkFile.ts) and the same add to cart. The helpers are kept
 * in step with product.js by hand; nothing is imported from it, so a cached
 * copy of one never runs against the other.
 *
 * The markup is sections/greaseproof-product.liquid, which renders the
 * selected variant's state and a JSON script ([data-product-json]) with every
 * variant: id, sku, option1 (Paper), option2 (Print), option3 (Quantity),
 * price in pence, sheets, lead_time, artwork_fee and available. This module
 * reads it and owns the panel from then on. Nothing is fetched.
 *
 * Price 0 means "not priced yet": such a run shows "Price coming soon" in the
 * total, no price per sheet, and Add to basket stays disabled; addToCart
 * refuses it too, whatever the button says.
 *
 * Add to cart: with no file attached, the line (and the artwork-setup line
 * when the run carries a fee) goes through /cart/add.js via
 * window.EraPackCart.add from cart.js, which opens the drawer. With a file,
 * the product form submits for real (the multipart POST to /cart/add is the
 * only way Shopify stores a file property) with return_to = this page +
 * ?variant=<id>&added=1; cart.js adds any fee line and opens the drawer on
 * that load. Every add carries a fresh properties[_uid].
 */

const GBP = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });
const EN_GB = new Intl.NumberFormat("en-GB");

/**
 * Thousandths of a pound a sheet, from the run's price in pence: pence x 10 /
 * sheets rounded half up in integers, floor((pence * 10 + floor(sheets / 2)) /
 * sheets), exactly as greaseproof-product.liquid and snippets/cart-line.liquid
 * (toFixed on a float can round the other way).
 */
function perSheetThou(pence, sheets) {
  const p = Math.round(Number(pence) || 0);
  const n = Math.round(Number(sheets) || 0);
  if (n <= 0) return 0;
  return Math.floor((p * 10 + Math.floor(n / 2)) / n);
}

/** "£0.013" (or "£1.250"): three decimals, from perSheetThou. */
function perSheetLabel(thou) {
  return `£${Math.floor(thou / 1000)}.${String(thou % 1000).padStart(3, "0")}`;
}

/** Pounds (a float) to "£1,234.56" (lib/format.ts gbp). */
function gbp(n) {
  return GBP.format(n);
}

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * The "Lead time" line property, from the variant's lead_time (snippets/delivery-label.liquid):
 * "4 to 5 working days" -> "4 to 5 working days from proof approval"; "2 to 3 weeks" -> "2 to 3 weeks
 * from proof approval"; event cups' "2 days" -> "delivered 2 days after proof approval".
 */
function deliveryLabel(leadTime) {
  const t = String(leadTime ?? "").toLowerCase().trim();
  if (!t) return "";
  return /working|week/.test(t) ? `${t} from proof approval` : `delivered ${t} after proof approval`;
}

/* ── lib/analytics.ts ────────────────────────────────────────────────────── */

function track(name, params = {}) {
  const g = typeof window !== "undefined" ? window.gtag : null;
  if (typeof g !== "function") return;
  g("event", name, { currency: "GBP", ...params });
}

/* ── lib/artworkFile.ts (as product.js) ──────────────────────────────────── */

// Shopify's cart refuses a multipart add carrying a larger file, so 3.5 MB is
// the ceiling (product.js has the measurements).
const ARTWORK_MAX_BYTES = 3.5 * 1024 * 1024;
const ARTWORK_TYPES = /^(image\/(png|jpe?g|svg\+xml|x-eps|eps)|application\/(pdf|postscript|illustrator|eps|x-eps))$/i;
const ARTWORK_EXTENSIONS = /\.(png|jpe?g|svg|pdf|ai|eps)$/i;
const ARTWORK_TOO_BIG = "That file is over 3.5MB. Export it smaller, or email it to hello@erapack.uk.";

/**
 * Checks (and, for big rasters, shrinks) the chosen print file. Resolves
 * with the File to upload, never over 3.5 MB: the original when it is within
 * 3.5 MB, otherwise a PNG/JPG redrawn smaller (2400 px on the long side, then
 * smaller steps down to 1000 px) until it fits. Rejects with a message to show.
 */
function readArtworkFile(file) {
  // PDF, AI, EPS, SVG, PNG or JPG only (the cart stores all six: tested
  // 28 Oct 2026 with .ai and .eps), by MIME type or, when the browser gives none
  // (or a generic one), by the file name's extension.
  if (!ARTWORK_TYPES.test(file.type || "") && !ARTWORK_EXTENSIONS.test(file.name || "")) {
    return Promise.reject(new Error("PDF, AI, EPS, SVG, PNG or JPG only, or email it to us after you order."));
  }
  if (file.size <= ARTWORK_MAX_BYTES) return Promise.resolve(file);
  const isRaster = /^image\/(png|jpe?g)$/.test(file.type);
  if (!isRaster) return Promise.reject(new Error(ARTWORK_TOO_BIG));

  // JPEG for photos, PNG for graphics with transparency.
  const type = file.type === "image/png" ? "image/png" : "image/jpeg";
  const draw = (img, side) =>
    new Promise((resolve) => {
      const scale = Math.min(1, side / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(resolve, type, type === "image/jpeg" ? 0.88 : undefined);
    });

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      URL.revokeObjectURL(url);
      for (const side of [2400, 1800, 1500, 1200, 1000]) {
        const blob = await draw(img, side);
        if (blob && blob.size > 0 && blob.size <= ARTWORK_MAX_BYTES) {
          resolve(new File([blob], file.name, { type, lastModified: Date.now() }));
          return;
        }
      }
      reject(new Error(ARTWORK_TOO_BIG));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That image could not be opened — try a PNG or JPG."));
    };
    img.src = url;
  });
}

/* ── the buy panel ───────────────────────────────────────────────────────── */

function setupGreaseproof(root) {
  const jsonEl = root.querySelector("[data-product-json]");
  if (!jsonEl) return;
  let data;
  try {
    data = JSON.parse(jsonEl.textContent);
  } catch {
    return;
  }

  const form = root.querySelector("[data-product-form]");
  const idInput = root.querySelector("[data-variant-id]");
  const stage = root.querySelector("[data-stage-img]");
  const subtitle = root.querySelector("[data-subtitle]");
  const list = root.querySelector("[data-qty-list]");
  const totalBox = root.querySelector("[data-total-box]");
  const totalPrice = root.querySelector("[data-total-price]");
  const totalEx = root.querySelector("[data-total-ex]");
  const totalVat = root.querySelector("[data-total-vat]");
  const totalPer = root.querySelector("[data-total-per]");
  const totalWith = root.querySelector("[data-total-with]");
  const totalSep = root.querySelector("[data-total-sep]");
  const totalDates = root.querySelector("[data-total-dates]");
  const feeNote = root.querySelector("[data-fee-note]");
  const feeAmount = root.querySelector("[data-fee-amount]");
  const deliveryLine = root.querySelector("[data-delivery-line]");
  const deliveryAmount = root.querySelector("[data-delivery-amount]");
  const deliveryBoxes = root.querySelector("[data-delivery-boxes]");
  const atc = root.querySelector("[data-atc]");
  const brief = root.querySelector("[data-artwork-brief]");
  const paperInputs = Array.from(root.querySelectorAll("[data-paper-input]"));
  const printInputs = Array.from(root.querySelectorAll("[data-print-input]"));
  const sizeLinks = Array.from(root.querySelectorAll("[data-size-link]"));
  const addLabel = (atc && atc.dataset.addLabel) || "Add to basket";
  const soonLabel = (atc && atc.dataset.soonLabel) || "Price coming soon";

  // An option value may arrive as a product_option_value object ({ name })
  // rather than a string: every option is read as its name.
  const optName = (x) => (x && typeof x === "object" ? String(x.name ?? x.value ?? "") : String(x ?? ""));
  const papers = Array.isArray(data.papers) ? data.papers : [];
  const prints = Array.isArray(data.prints) ? data.prints : [];
  const variants = (Array.isArray(data.variants) ? data.variants : []).map((v) => ({
    ...v,
    option1: optName(v.option1),
    option2: optName(v.option2),
    option3: optName(v.option3),
    price: Number(v.price) || 0,
    sheets: Number(v.sheets) || 0,
  }));
  if (!variants.length) return;

  const paperKeyOf = (title) => (papers.find((p) => String(p.title) === String(title)) || {}).key || "";
  const printKeyOf = (title) => (prints.find((p) => String(p.title) === String(title)) || {}).key || "";
  const paperTitle = (key) => (papers.find((p) => p.key === key) || papers[0] || {}).title;
  const printTitle = (key) => (prints.find((p) => p.key === key) || prints[0] || {}).title;
  const priced = (v) => !!v && v.price > 0;

  /* state: the paper and print keys, and the run in sheets */
  const selected = variants.find((v) => String(v.id) === String(data.selected)) || variants[0];
  let paper = paperKeyOf(selected.option1) || (papers[0] && papers[0].key) || "";
  let print = printKeyOf(selected.option2) || (prints[0] && prints[0].key) || "";
  let sheets = selected.sheets;
  let frame = 0;
  let adding = false;
  let changed = false; // the page URL follows the choice only once the visitor changes it

  /** This paper and print's runs, smallest first. */
  const ladder = () =>
    variants
      .filter((v) => v.option1 === paperTitle(paper) && v.option2 === printTitle(print))
      .sort((a, b) => a.sheets - b.sheets);

  /** The run with this many sheets, or the nearest one sold (a tie goes to the smaller). */
  const nearest = (l, wanted) => {
    let best = null;
    l.forEach((x) => {
      const d = Math.abs(x.sheets - wanted);
      const bestD = best ? Math.abs(best.sheets - wanted) : Infinity;
      if (d < bestD || (d === bestD && x.sheets < best.sheets)) best = x;
    });
    return best;
  };
  const current = () => {
    const l = ladder();
    return l.find((x) => x.sheets === sheets) || nearest(l, sheets) || l[0] || null;
  };
  const perSheet = (v) => (v && v.sheets > 0 ? perSheetThou(v.price, v.sheets) : 0);
  const sheetsLabel = (v) => (v ? EN_GB.format(v.sheets) : "");

  // Shopify's ?variant=<id> wins (Liquid has already painted it); otherwise
  // ?paper=W|K, ?print=1C|2C|4C and ?sheets=<n> (the size links) pick
  // the run, each over the page's default.
  try {
    const params = new URLSearchParams(window.location.search);
    const wantedVariant = params.get("variant");
    const v = wantedVariant ? variants.find((x) => String(x.id) === String(wantedVariant)) : null;
    if (v) {
      paper = paperKeyOf(v.option1) || paper;
      print = printKeyOf(v.option2) || print;
      sheets = v.sheets;
    } else {
      const wantedPaper = String(params.get("paper") || "").toUpperCase();
      const wantedPrint = String(params.get("print") || "").toUpperCase();
      const wantedSheets = Number(params.get("sheets"));
      if (papers.some((p) => p.key === wantedPaper)) paper = wantedPaper;
      if (prints.some((p) => p.key === wantedPrint)) print = wantedPrint;
      if (wantedSheets > 0) sheets = wantedSheets;
    }
  } catch {
    /* no URL access: keep the Liquid defaults */
  }
  {
    const v = current();
    if (v) sheets = v.sheets;
  }

  /**
   * A size link's address for this paper, print and run: that size's own
   * ?variant=<id> from its data-variants (78 ids, Paper-major, then Print,
   * then Quantity, as this page's papers, prints and rungs), which Liquid
   * paints on arrival; the three keys only when the ids are missing.
   */
  const rungs = (Array.isArray(data.rungs) ? data.rungs : []).map(optName);
  const sizeHref = (a, v) => {
    const ids = String(a.dataset.variants || "").split(",").filter(Boolean);
    const pi = papers.findIndex((p) => p.key === paper);
    const ri = prints.findIndex((p) => p.key === print);
    const qi = rungs.indexOf(v.option3);
    const id = ids.length === papers.length * prints.length * rungs.length && pi >= 0 && ri >= 0 && qi >= 0
      ? ids[(pi * prints.length + ri) * rungs.length + qi]
      : "";
    return id ? `${a.dataset.href}?variant=${encodeURIComponent(id)}` : `${a.dataset.href}?${choiceQuery()}`;
  };

  /** The query a size link carries without variant ids: this paper, print and run. */
  const choiceQuery = () => {
    const q = new URLSearchParams();
    q.set("paper", paper);
    q.set("print", print);
    q.set("sheets", String(sheets));
    return q.toString();
  };

  /** The quantity chips: one button per run, the price a sheet under it ("£0.120") once priced. */
  function renderList() {
    if (!list) return;
    const l = ladder();
    const cur = current();
    // Update the chips in place so the one just pressed keeps keyboard and
    // screen-reader focus; only add or remove chips when the ladder length
    // changes.
    const focusedRow = list.contains(document.activeElement)
      ? document.activeElement.getAttribute("data-qty-row")
      : null;
    const existing = Array.from(list.querySelectorAll("[data-qty-row]"));
    l.forEach((v, i) => {
      let b = existing[i];
      if (!b) {
        b = document.createElement("button");
        b.type = "button";
        b.className = "pm-a__chip";
        b.append(document.createElement("strong"));
        list.appendChild(b);
      }
      // "1,000" plus " sheets" for screen readers, priced or not.
      const strong = b.querySelector("strong");
      const sr = document.createElement("span");
      sr.className = "sr-only";
      sr.textContent = " sheets";
      strong.replaceChildren(document.createTextNode(sheetsLabel(v)), sr);
      b.setAttribute("aria-pressed", cur && v.id === cur.id ? "true" : "false");
      b.setAttribute("data-qty-row", String(i));
      b.setAttribute("data-variant", String(v.id));
      let span = b.querySelector(":scope > span");
      if (priced(v)) {
        if (!span) {
          span = document.createElement("span");
          b.appendChild(span);
        }
        const srs = document.createElement("span");
        srs.className = "sr-only";
        srs.textContent = " a sheet";
        span.replaceChildren(document.createTextNode(perSheetLabel(perSheet(v))), srs);
      } else if (span) {
        span.remove();
      }
    });
    existing.slice(l.length).forEach((b) => b.remove());
    if (focusedRow !== null && !list.contains(document.activeElement)) {
      const again = list.querySelector(`[data-qty-row="${focusedRow}"]`);
      if (again) again.focus();
    }
  }

  /** The photo the thumb picks; every photo fills the stage's 4:3 frame (the stylesheet). */
  function renderMedia() {
    const thumbs = Array.from(root.querySelectorAll("[data-thumb]"));
    const t = thumbs[frame];
    if (!t || !stage) return;
    if (t.dataset.src && stage.getAttribute("src") !== t.dataset.src) stage.setAttribute("src", t.dataset.src);
    stage.setAttribute("alt", t.dataset.alt || "");
    thumbs.forEach((b, i) => b.setAttribute("aria-current", i === frame ? "true" : "false"));
  }

  function render() {
    const l = ladder();
    const v = current();
    if (!v) return;
    sheets = v.sheets;

    paperInputs.forEach((input) => {
      input.checked = input.value === paper;
    });
    printInputs.forEach((input) => {
      input.checked = input.value === print;
    });
    if (idInput) idInput.value = String(v.id);

    // The line under the title: the smallest priced run, then the cheapest
    // a sheet (a mid run can beat the largest; the smallest run on a tie),
    // as greaseproof-product.liquid renders it; hidden with none priced.
    const pricedRuns = l.filter((x) => priced(x) && x.sheets > 0);
    const first = pricedRuns.reduce((a, x) => (!a || x.sheets < a.sheets ? x : a), null);
    const last = pricedRuns.reduce((a, x) => {
      if (!a) return x;
      const d = perSheet(x) - perSheet(a);
      return d < 0 || (d === 0 && x.sheets < a.sheets) ? x : a;
    }, null);
    if (subtitle) {
      subtitle.hidden = !first;
      subtitle.textContent = !first
        ? ""
        : first !== last
          ? `${perSheetLabel(perSheet(first))} a sheet at ${sheetsLabel(first)}, down to ${perSheetLabel(perSheet(last))} at ${sheetsLabel(last)}`
          : `${perSheetLabel(perSheet(first))} a sheet at ${sheetsLabel(first)}`;
    }

    renderList();

    // The size links take this choice to the other sizes. Liquid wrote them
    // for its own variant (?variant= of the same choice there); once the run
    // differs from that one (a change, or the URL's keys), each becomes that
    // size's ?variant= of the new choice (sizeHref).
    if (changed || String(v.id) !== String(data.selected)) {
      sizeLinks.forEach((a) => {
        if (a.dataset.href) a.setAttribute("href", sizeHref(a, v));
      });
    }

    const isPriced = priced(v);
    const subtotal = v.price / 100;
    const fee = Number(v.artwork_fee) || 0;
    const vat = round2((subtotal + fee) * 0.2); // VAT on the run and the artwork setup, as checkout charges
    if (totalBox) totalBox.classList.toggle("gpp__soon", !isPriced);
    if (totalPrice) totalPrice.textContent = isPriced ? gbp(subtotal + fee) : soonLabel;
    if (totalEx) totalEx.hidden = !isPriced;
    if (totalVat) {
      // The run's price a sheet beside the total, then the total with VAT.
      totalVat.hidden = !isPriced;
      const withVat = isPriced ? `${gbp(round2(subtotal + fee + vat))} with VAT` : "";
      const per = isPriced && v.sheets > 0 ? `${perSheetLabel(perSheet(v))} a sheet` : "";
      if (totalPer && totalWith) {
        totalPer.textContent = per;
        totalPer.hidden = !per;
        if (totalSep) totalSep.hidden = !per;
        totalWith.textContent = withVat;
      } else {
        totalVat.textContent = per ? `${per} · ${withVat}` : withVat;
      }
    }
    if (feeNote) feeNote.hidden = !(isPriced && fee > 0);
    if (feeAmount && fee > 0) feeAmount.textContent = `${gbp(fee)} artwork setup`;
    // Delivery to mainland UK: the run's boxes at the rate a box, as the
    // basket charges it (sections/greaseproof-product.liquid).
    if (deliveryLine) {
      const boxes = Math.max(0, Number(v.boxes) || 0);
      const rate = Number(data.carriage) || 4;
      deliveryLine.hidden = !(isPriced && boxes > 0);
      if (boxes > 0) {
        if (deliveryAmount) deliveryAmount.textContent = gbp(boxes * rate);
        if (deliveryBoxes) deliveryBoxes.textContent = `(${boxes} ${boxes === 1 ? "box" : "boxes"} × ${gbp(rate)})`;
      }
    }
    if (totalDates) totalDates.textContent = v.lead_time ? `${v.lead_time} lead time` : "";

    // The artwork brief only for a run that can be ordered.
    if (brief) brief.hidden = !isPriced;

    if (atc) {
      atc.textContent = isPriced ? addLabel : soonLabel;
      atc.disabled = adding || !isPriced || v.available === false;
    }

    // The address keeps the choice (reload, share, back from the basket),
    // once the visitor has made one: the first render leaves ?added=1 for
    // cart.js to read.
    if (changed) {
      try {
        const url = new URL(window.location.href);
        ["paper", "print", "sheets"].forEach((k) => url.searchParams.delete(k));
        url.searchParams.set("variant", String(v.id));
        window.history.replaceState(window.history.state, "", url.toString());
      } catch {
        /* no history access: the links still carry the choice */
      }
    }
  }

  /* handlers */
  paperInputs.forEach((input) => {
    input.addEventListener("change", () => {
      if (!input.checked) return;
      if (papers.some((p) => p.key === input.value)) paper = input.value;
      changed = true;
      render();
    });
  });
  printInputs.forEach((input) => {
    input.addEventListener("change", () => {
      if (!input.checked) return;
      if (prints.some((p) => p.key === input.value)) print = input.value;
      changed = true;
      render();
    });
  });

  root.addEventListener("click", (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const thumb = t.closest("[data-thumb]");
    if (thumb && root.contains(thumb)) {
      frame = Number(thumb.getAttribute("data-thumb")) || 0;
      renderMedia();
      return;
    }
    const row = t.closest("[data-qty-row]");
    if (row && root.contains(row)) {
      const v = ladder()[Number(row.getAttribute("data-qty-row")) || 0];
      if (v) sheets = v.sheets;
      changed = true;
      render();
    }
  });

  /* the artwork brief */
  const tile = root.querySelector("[data-artwork-tile]");
  const fileInput = root.querySelector("[data-artwork-input]");
  const nameEl = root.querySelector("[data-artwork-name]");
  const hintEl = root.querySelector("[data-artwork-hint]");
  let artworkError = null;

  function renderArtwork() {
    const file = fileInput && fileInput.files && fileInput.files[0];
    const has = !!(file && file.size);
    if (tile) {
      tile.classList.toggle("has-file", has);
      tile.classList.toggle("has-error", !!artworkError);
    }
    const ds = fileInput ? fileInput.dataset : {};
    if (nameEl) nameEl.textContent = has ? file.name : ds.emptyLabel || "Upload your logo or artwork";
    if (hintEl) {
      hintEl.textContent =
        artworkError ||
        (has
          ? ds.attachedHint || "Attached to this order. Click to swap it."
          : ds.emptyHint || "PDF, AI, EPS or SVG prints sharpest. PNG or JPG work too, up to 3.5 MB");
    }
  }

  if (fileInput) {
    fileInput.addEventListener("change", () => {
      const file = fileInput.files && fileInput.files[0];
      if (!file) return;
      artworkError = null;
      renderArtwork();
      readArtworkFile(file).then(
        (result) => {
          if (result !== file) {
            let swapped = false;
            try {
              const dt = new DataTransfer();
              dt.items.add(result);
              fileInput.files = dt.files;
              swapped = fileInput.files[0] === result || fileInput.files[0].size === result.size;
            } catch {
              swapped = false;
            }
            // A browser that cannot swap the file would upload the big
            // original, which Shopify refuses: ask for a smaller export.
            if (!swapped) {
              fileInput.value = "";
              artworkError = ARTWORK_TOO_BIG;
              renderArtwork();
              return;
            }
          }
          artworkError = null;
          renderArtwork();
        },
        (err) => {
          fileInput.value = "";
          artworkError = err && err.message ? err.message : "Could not read that file — try again.";
          renderArtwork();
        },
      );
    });
  }

  /* add to cart */

  /** A per-add id, so two identical adds stay two lines (the site's uid). */
  const newUid = () =>
    `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

  /** The artwork-setup companion line, for a run that carries the fee. */
  function feeItem(v) {
    const fee = Number(v.artwork_fee) || 0;
    const feeVariant = Number(data.artworkVariant) || 0;
    if (fee <= 0 || !feeVariant) return null;
    return { id: feeVariant, quantity: 1, properties: { For: `${data.title} · ${v.option1} · ${v.option2}` } };
  }

  /** The paper and print radios are the page's, not the line's: kept off a real submit. */
  const setChoiceInputsDisabled = (off) => {
    paperInputs.concat(printInputs).forEach((input) => {
      input.disabled = off;
    });
  };

  async function addToCart(e) {
    if (e) e.preventDefault();
    if (adding) return;
    const v = current();
    if (!v || !form) return;
    // Never sold before it has a price, whatever the button says.
    if (!priced(v) || v.available === false) {
      render();
      return;
    }

    const file = fileInput && fileInput.files && fileInput.files[0];
    const hasFile = !!(file && file.size);
    const notes = root.querySelector("[data-artwork-notes]");
    const text = notes ? String(notes.value || "").trim() : "";
    const uid = newUid();
    const fee = feeItem(v);

    adding = true;
    render();
    track("add_to_cart", {
      currency: "GBP",
      value: round2(v.price / 100 + (Number(v.artwork_fee) || 0)),
      items: [
        {
          item_id: v.sku || String(v.id),
          item_name: `${data.size} greaseproof paper`,
          item_variant: `${v.option1} / ${v.option2} / ${v.option3 || `${sheetsLabel(v)} sheets`}`,
          price: round2(v.price / 100),
          quantity: 1,
        },
      ],
    });

    try {
      if (hasFile) {
        // Shopify stores a file property only from the product form's own
        // multipart POST to /cart/add. The form submits for real and comes
        // back here with ?added=1; cart.js then adds the artwork-setup fee
        // line the run needs (its reconcile on load) and opens the drawer.
        if (file.size > ARTWORK_MAX_BYTES) throw new Error(ARTWORK_TOO_BIG);
        if (idInput) idInput.value = String(v.id);
        const uidInput = form.querySelector("[data-line-uid]");
        if (uidInput) uidInput.value = uid;
        const deliveryInput = form.querySelector("[data-line-delivery]");
        if (deliveryInput) deliveryInput.value = deliveryLabel(v.lead_time);
        const back = form.querySelector("[data-return-to]");
        if (back) {
          const q = new URLSearchParams();
          q.set("variant", String(v.id));
          q.set("added", "1");
          back.value = `${window.location.pathname}?${q.toString()}`;
        }
        // Only the line's own fields travel: no empty notes, no paper or print radio.
        if (notes) notes.disabled = !text;
        setChoiceInputsDisabled(true);
        HTMLFormElement.prototype.submit.call(form);
        return; // the page navigates; `adding` stays on
      }

      const properties = { _uid: uid };
      const delivery = deliveryLabel(v.lead_time);
      if (delivery) properties["Lead time"] = delivery;
      if (text) properties["Design preferences"] = text;
      const items = [{ id: Number(v.id), quantity: 1, properties }];
      if (fee) items.push(fee);
      const cart = window.EraPackCart;
      if (cart && typeof cart.add === "function") {
        await cart.add(items);
        if (typeof cart.open === "function") cart.open();
      } else {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error("Could not add that to the cart. Please try again.");
        window.location.href = data.cartUrl || "/cart";
      }
      adding = false;
      render();
    } catch (err) {
      adding = false;
      if (notes) notes.disabled = false;
      setChoiceInputsDisabled(false);
      render();
      const message = err && err.message ? err.message : "Could not add that to the cart. Please try again.";
      if (hintEl && /upload|file|artwork/i.test(message)) {
        artworkError = message;
        renderArtwork();
      } else {
        window.alert(message);
      }
    }
  }

  if (form) form.addEventListener("submit", addToCart);
  // Back from the cart through the bfcache after a file upload: unlock.
  window.addEventListener("pageshow", (e) => {
    if (!e.persisted) return;
    adding = false;
    const notes = root.querySelector("[data-artwork-notes]");
    if (notes) notes.disabled = false;
    setChoiceInputsDisabled(false);
    render();
  });

  render();
  renderArtwork();
}

function init() {
  document.querySelectorAll("[data-gp-product]").forEach(setupGreaseproof);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}

// Theme editor: a section re-rendered in place needs its behaviour again.
document.addEventListener("shopify:section:load", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target) return;
  target.querySelectorAll("[data-gp-product]").forEach(setupGreaseproof);
});
